import React, { useEffect, useState } from 'react';

const STEPS = [
  {
    type: 'lesson',
    narration:
      'In terms of compensation, you’ll earn fifteen dollars for every referral who attends their first gathering. You can also unlock monthly bonuses by reaching higher referral tiers. At ten qualified referrals, you’ll earn one hundred fifty dollars total. At twenty, you’ll earn four hundred dollars; at thirty, six hundred; at forty, eight hundred fifty; and at fifty, one thousand one hundred fifty dollars. These totals include your referral earnings and bonus. Only the highest bonus tier you reach is applied, so bonuses do not stack. Your qualified referral count resets each month, and referrals above fifty continue earning fifteen dollars each.',
  },
];

const TIERS = [
  { referrals: '10', payout: '$150' },
  { referrals: '20', payout: '$400' },
  { referrals: '30', payout: '$600' },
  { referrals: '40', payout: '$850' },
  { referrals: '50', payout: '$1,150' },
];

const STAGE_TIMINGS = [
  1700,
  6200,
  10100,
  14300,
  17000,
  18400,
  20100,
];

export default function Compensation({
  audioStatus,
  playbackId,
}) {
  const [visualStage, setVisualStage] = useState(0);

  useEffect(() => {
    if (audioStatus !== 'Playing') {
      return undefined;
    }

    setVisualStage(0);

    const timers = STAGE_TIMINGS.map(
      (time, index) =>
        window.setTimeout(
          () => setVisualStage(index + 1),
          time
        )
    );

    return () => {
      timers.forEach(window.clearTimeout);
    };
  }, [audioStatus, playbackId]);

  const tierCount = Math.max(
    0,
    Math.min(TIERS.length, visualStage - 2)
  );

  return (
    <section className="compensation-kinetic-slide">
      <style>{STYLES}</style>

      <IntroCaption
        active={visualStage === 0}
      />

      <BaseRate
        active={visualStage === 1}
      />

      <BonusCaption
        active={visualStage === 2}
      />

      <TierSequence
        active={visualStage >= 3}
        visibleCount={tierCount}
      />
    </section>
  );
}

function IntroCaption({ active }) {
  return (
    <div className={sceneClass(active)}>
      <WordCaption
        text="In terms of compensation"
        wordDelay={250}
      />
    </div>
  );
}

function BaseRate({ active }) {
  return (
    <div className={sceneClass(active, 'base-rate-scene')}>
      <div className="base-rate-lockup">
        <span>$</span>
        <strong>15</strong>

        <div>
          <small>
            <WordLine
              text="for every"
              wordDelay={150}
            />
          </small>

          <b>
            <WordLine
              text="qualified referral"
              wordDelay={170}
            />
          </b>
        </div>
      </div>

      <i className="base-rate-rule" />

    </div>
  );
}

function BonusCaption({ active }) {
  return (
    <div className={sceneClass(active)}>
      <WordCaption
        text="You can also unlock monthly bonuses"
        wordDelay={230}
        handwrittenWords={['bonuses']}
      />
    </div>
  );
}

function TierSequence({ active, visibleCount }) {
  return (
    <div className={sceneClass(active, 'tier-sequence-scene')}>
      <div className="horizontal-tiers">
        {TIERS.map((tier, index) => {
          const visible = index < visibleCount;
          const current = index === visibleCount - 1;

          return (
            <div
              className={[
                'horizontal-tier',
                visible ? 'is-visible' : '',
                current ? 'is-current' : '',
              ].join(' ')}
              key={tier.referrals}
            >
              <span>{tier.referrals}</span>
              <i />
              <strong>{tier.payout}</strong>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WordCaption({
  text,
  wordDelay,
  handwrittenWords = [],
}) {
  const words = text.split(' ');

  return (
    <h1 key={text} aria-label={text}>
      {words.map((word, index) => (
        <React.Fragment key={`${word}-${index}`}>
          <span
            className={[
              'compensation-caption-word',
              handwrittenWords.includes(
                word.replace(/[^A-Za-z]/g, '')
              )
                ? 'is-handwritten'
                : '',
            ].join(' ')}
            style={{
              '--compensation-word-delay': `${index * wordDelay}ms`,
            }}
            aria-hidden="true"
          >
            {word}
          </span>

          {index < words.length - 1 ? ' ' : ''}
        </React.Fragment>
      ))}
    </h1>
  );
}

function WordLine({ text, wordDelay }) {
  const words = text.split(' ');

  return words.map((word, index) => (
    <React.Fragment key={`${word}-${index}`}>
      <span
        className="compensation-caption-word"
        style={{
          '--compensation-word-delay': `${index * wordDelay}ms`,
        }}
        aria-hidden="true"
      >
        {word}
      </span>

      {index < words.length - 1 ? ' ' : ''}
    </React.Fragment>
  ));
}

function sceneClass(active, extra = '') {
  return [
    'compensation-scene',
    active ? 'is-active' : '',
    extra,
  ].filter(Boolean).join(' ');
}

Compensation.title = 'Compensation';
Compensation.steps = STEPS;

const STYLES = `
  .compensation-kinetic-slide {
    position: relative;
    min-height: 465px;
    width: 100%;
    overflow: hidden;
    color: #111;
    text-rendering: optimizeLegibility;
  }

  .compensation-scene {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transform: translateY(25px);
    transition:
      opacity 480ms ease,
      transform 680ms cubic-bezier(.18,.84,.22,1);
    pointer-events: none;
  }

  .compensation-scene.is-active {
    opacity: 1;
    transform: translateY(0);
  }

  .compensation-scene h1 {
    max-width: 820px;
    margin: 0;
    text-align: center;
    font-size: clamp(40px, 4.9vw, 64px);
    font-weight: 800;
    letter-spacing: -0.048em;
    line-height: 1;
  }

  .compensation-caption-word {
    display: inline-block;
    opacity: 0;
    transform: translateY(0.38em);
  }

  .compensation-caption-word.is-handwritten {
    margin-left: 0.025em;
    font-family: 'Ugly Handwriting', cursive;
    font-size: 1.16em;
    font-weight: 400;
    letter-spacing: -0.01em;
    line-height: 0.82;
  }

  .compensation-scene.is-active .compensation-caption-word {
    animation: compensation-word-in 330ms cubic-bezier(.18,.84,.22,1) forwards;
    animation-delay: var(--compensation-word-delay);
  }

  .base-rate-scene {
    flex-direction: column;
  }

  .base-rate-lockup {
    display: grid;
    grid-template-columns: auto auto 1fr;
    align-items: end;
    gap: 12px;
  }

  .base-rate-lockup > span {
    padding-bottom: 13px;
    font-size: clamp(36px, 4.6vw, 56px);
    font-weight: 700;
    opacity: 0;
    transform: translateY(20px);
  }

  .base-rate-lockup > strong {
    font-size: clamp(112px, 14vw, 170px);
    font-weight: 800;
    letter-spacing: -0.07em;
    line-height: 0.74;
    opacity: 0;
    transform: translateY(35px);
  }

  .base-rate-lockup > div {
    display: grid;
    gap: 6px;
    padding: 0 0 7px 21px;
    opacity: 0;
    transform: translateY(18px);
  }

  .base-rate-lockup small {
    color: #777;
    font-size: 12px;
    font-weight: 620;
    letter-spacing: 0.015em;
  }

  .base-rate-lockup b {
    font-size: clamp(19px, 2.25vw, 28px);
    font-weight: 760;
    letter-spacing: -0.03em;
  }

  .base-rate-scene.is-active .base-rate-lockup > span,
  .base-rate-scene.is-active .base-rate-lockup > strong,
  .base-rate-scene.is-active .base-rate-lockup > div {
    animation: compensation-rise-in 650ms cubic-bezier(.18,.84,.22,1) forwards;
  }

  .base-rate-scene.is-active .base-rate-lockup > strong {
    animation-delay: 90ms;
  }

  .base-rate-scene.is-active .base-rate-lockup > div {
    animation-delay: 260ms;
  }

  .base-rate-rule {
    width: min(100%, 680px);
    height: 1px;
    margin-top: 35px;
    background: #bdbdbd;
    transform: scaleX(0);
    transform-origin: left;
  }

  .base-rate-scene.is-active .base-rate-rule {
    animation: compensation-line-in 900ms 350ms cubic-bezier(.18,.84,.22,1) forwards;
  }

  .tier-sequence-scene {
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    padding: 0 16px;
  }

  .horizontal-tiers {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    width: 100%;
    padding-top: 0;
  }

  .horizontal-tier {
    display: grid;
    grid-template-rows: auto 52px auto;
    justify-items: center;
    opacity: 0.18;
    transition:
      opacity 500ms ease,
      transform 650ms cubic-bezier(.18,.84,.22,1);
  }

  .horizontal-tier span {
    font-size: clamp(30px, 3.8vw, 46px);
    font-weight: 790;
    letter-spacing: -0.05em;
    font-variant-numeric: tabular-nums;
  }

  .horizontal-tier i {
    width: 1px;
    height: 0;
    margin: 8px 0;
    background: #111;
    transition: height 600ms cubic-bezier(.18,.84,.22,1);
  }

  .horizontal-tier strong {
    font-family: inherit;
    font-size: clamp(18px, 2.05vw, 27px);
    font-weight: 740;
    letter-spacing: -0.025em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    transform: translateY(9px);
    transition: transform 550ms cubic-bezier(.18,.84,.22,1);
  }

  .horizontal-tier.is-visible {
    opacity: 1;
  }

  .horizontal-tier.is-visible i {
    height: 38px;
  }

  .horizontal-tier.is-visible strong {
    transform: translateY(0);
  }

  .horizontal-tier.is-current span,
  .horizontal-tier.is-current strong {
    color: #111;
  }

  @keyframes compensation-word-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes compensation-rise-in {
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes compensation-line-in {
    to {
      transform: scaleX(1);
    }
  }

  @media (max-width: 720px) {
    .compensation-kinetic-slide {
      min-height: 500px;
    }

    .base-rate-lockup {
      grid-template-columns: auto auto;
    }

    .base-rate-lockup > div {
      grid-column: 1 / -1;
      padding: 22px 0 0;
    }

    .tier-sequence-scene {
      padding: 0;
    }

    .horizontal-tier span {
      font-size: 28px;
    }

    .horizontal-tier strong {
      font-size: 16px;
    }
  }

  @media (max-width: 470px) {
    .compensation-scene h1 {
      font-size: 39px;
    }

    .horizontal-tiers {
      padding-top: 0;
    }

    .horizontal-tier span {
      font-size: 23px;
    }

    .horizontal-tier strong {
      font-size: 14px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .compensation-scene,
    .compensation-kinetic-slide * {
      animation-duration: 0.01ms !important;
      animation-delay: 0ms !important;
      transition-duration: 0.01ms !important;
    }
  }
`;
